/**
 * useUploadFlow — Step 0's upload lifecycle.
 *
 * Composes `uploadDocument` + `startAnalysis` against the reducer's
 * Step-0 slot. Drives `status` empty → generating → approved | failed
 * via `actions.setStepStatus`, and surfaces the new `thread_id` via
 * a `newThreadId` getter that the orchestrator consumes to override
 * the prop-level threadId for this mount.
 *
 * Story 4-11 don't reuse this hook directly — only Step 0 has an
 * upload concept. It's extracted to keep WorkflowV2.jsx (now .tsx)
 * thin and to mirror the per-flow hook pattern Stories 4-11 will
 * use for their own per-step lifecycles.
 */
import { useCallback, useState } from 'react';
import { uploadDocument, startAnalysis } from '../../../api/documents';
import type { UploadFlowState } from '../step-components/stepBodyProps';
import type { StepStatus, WorkflowActions } from '../state/workflowV2Types';

const UPLOAD_STEP_ID = 0;

export interface UseUploadFlowInput {
  sessionId: string | null | undefined;
  tenantId: string | null | undefined;
  actions: WorkflowActions;
  /** Called with the new thread_id after `startAnalysis` succeeds. */
  onThreadIdResolved: (threadId: string) => void;
  /** Reducer-side status for Step 0 — mirrored into the returned `status` field. */
  status: StepStatus;
  errorMessage: string | null;
}

export function useUploadFlow({
  sessionId,
  tenantId,
  actions,
  onThreadIdResolved,
  status,
  errorMessage,
}: UseUploadFlowInput): UploadFlowState {
  // Local state isn't needed — the reducer owns Step 0's status,
  // and errorMessage rides along with it via SET_STEP_STATUS.
  const onStartAnalysis = useCallback(
    async (file: File) => {
      if (!sessionId || !tenantId) {
        actions.setStepStatus(UPLOAD_STEP_ID, 'failed', 'Missing session or tenant context.');
        return;
      }
      actions.setStepStatus(UPLOAD_STEP_ID, 'generating');
      try {
        const uploadResp: any = await uploadDocument(file, sessionId, tenantId);
        const fileId = uploadResp?.file_id;
        if (!fileId) throw new Error('Upload returned no file_id');
        const startResp: any = await startAnalysis(fileId, sessionId, tenantId);
        const newThreadId = startResp?.thread_id;
        if (!newThreadId) throw new Error('start-analysis returned no thread_id');
        onThreadIdResolved(newThreadId);
        actions.setStepStatus(UPLOAD_STEP_ID, 'approved');
        // Optimistic: Step 1's agent has just started on the backend.
        // Without this write, the rail circle stays at "1" until the
        // first polling tick (~3s later) catches up — the user sees a
        // numbered circle next to a Step-1 content panel instead of
        // the in-flight spinner. Polling will overwrite this with the
        // real backend status (still `generating`) shortly.
        actions.setStepStatus(1, 'generating');
        actions.selectStep(1);
      } catch (e: any) {
        actions.setStepStatus(
          UPLOAD_STEP_ID,
          'failed',
          e?.response?.data?.detail || e?.message || 'Failed to start analysis',
        );
      }
    },
    [sessionId, tenantId, actions, onThreadIdResolved],
  );

  return {
    status,
    error: errorMessage,
    onStartAnalysis,
  };
}
